import React from "react";
import PropTypes from "prop-types";
import styles from "./ReviewItem.module.css";
import icon from "./Identity_Icon.svg";
import verifiedIcon from "assets/Verified_Icon.svg";
import reviewIcon from "assets/Review_Icon.svg";
import requiredIcon from "assets/Required_Icon.svg";
import pendingIcon from "assets/Pending_Icon.svg";
import { useAccount } from "utils/AccountInfoContext";
import { useTranslation } from "react-i18next";

export function ReviewItem({
  address,
  addressLabel,
  accountName,
  nameOnAccount,
  lastFour,
  onReview,
}) {
  const { subEntityData } = useAccount();
  const {t} = useTranslation();

  const getBadgeIcon = (status) => {
    switch (status) {
      case 1:
      case 6:
      case 7:
        return verifiedIcon;
      case 2:
      case 4:
        return reviewIcon;
      case 3:
        return requiredIcon;
      case 5:
        return pendingIcon;
      default:
        return null;
    }
  };

  const getBadgeColor = (status) => {
    switch (status) {
      case 1:
      case 6:
      case 7:
        return "#39B54A";
      case 2:
      case 4:
        return "#D3B21B";
      case 3:
        return "#ED1F24";
      case 5:
        return "#91B6BB";
      default:
        return "#000";
    }
  };

  const handleClick = () => {
    alert("Icon clicked");
  };

  const currentEntity = subEntityData.find(
    (entity) => entity.name === accountName
  );

  if (!currentEntity) return null;

  const {
    emailStatus,
    addressStatus,
    achStatus,
    w9Status,
    bankRequired,
    w9Required,
    bankInfo,
  } = currentEntity;

  return (
    <div className={styles.container}>
      <span className={styles.addressLabel}>{addressLabel}</span>
      <div className={styles.addressDetails}>
        <span>{address.line1}</span>
        <span>{address.line2}</span>
        <div className={styles.cityStateZip}>
          <span>{address.city},&nbsp;</span>
          <span>{address.state}&nbsp;</span>
          <span>{address.postalCode}</span>
        </div>
      </div>
      <div className={styles.bottomContainer}>
        <div className={styles.iconContainer}>
          <img
            className={styles.icon}
            src={icon}
            alt="icon"
            onClick={handleClick}
          />
        </div>
        <div className={styles.accountInfo}>
          <div className={styles.accountDetails}>
            {bankInfo != null ? (
              <>
                <span className={styles.accountName}>{nameOnAccount}</span>
                <span className={styles.lastFour}>
                  {t("Account ending in")} {lastFour}
                </span>
              </>
            ) : (
              <span className={styles.lastFour}>
                {t("Bank Information Not Given")}
              </span>
            )}
          </div>
        </div>
      </div>
      <button className={styles.reviewButton} onClick={onReview}>
        {t("Review")}
      </button>
      <div className={styles.badgesContainer}>
        {emailStatus && (
          <div
            className={styles.badge}
            style={{ color: getBadgeColor(emailStatus) }}
          >
            <img
              className={styles.badgeIcon}
              src={getBadgeIcon(emailStatus)}
              alt="Email Status"
            />
            <span>{t("Email")}</span>
          </div>
        )}
        {addressStatus && (
          <div
            className={styles.badge}
            style={{ color: getBadgeColor(addressStatus) }}
          >
            <img
              className={styles.badgeIcon}
              src={getBadgeIcon(addressStatus)}
              alt="Address Status"
            />
            <span>{t("Address")}</span>
          </div>
        )}
        {bankRequired && achStatus && (
          <div
            className={styles.badge}
            style={{ color: getBadgeColor(achStatus) }}
          >
            <img
              className={styles.badgeIcon}
              src={getBadgeIcon(achStatus)}
              alt="Bank Status"
            />
            <span>{t("Bank")}</span>
          </div>
        )}
        {w9Required && w9Status !== 0 && (
          <div
            className={styles.badge}
            style={{ color: getBadgeColor(w9Status) }}
          >
            <img
              className={styles.badgeIcon}
              src={getBadgeIcon(w9Status)}
              alt="W9 Status"
            />
            <span>W9</span>
          </div>
        )}
      </div>
    </div>
  );
}

ReviewItem.propTypes = {
  address: PropTypes.shape({
    line1: PropTypes.string,
    line2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    postalCode: PropTypes.string,
  }).isRequired,
  addressLabel: PropTypes.string.isRequired,
  accountName: PropTypes.string.isRequired,
  lastFour: PropTypes.string.isRequired,
  onReview: PropTypes.func.isRequired,
};

export default ReviewItem;
